import { useState, useCallback, useRef, useEffect } from 'react';
import { isSensitiveLog } from '../lib/consoleFilter';
import { Cpu, Plug, DownloadCloud, Copy, Check, RefreshCw, HardDriveDownload, Upload } from 'lucide-react';
import { getSharedManager } from '../lib/serial';
import { fetchBridgePorts } from '../lib/bridge';
import { saveBinaryWithPicker } from '../lib/saveAs';
import { SaveAsDialog } from './SaveAsDialog';
import { toast } from 'sonner';

const BRIDGE_URL = 'ws://localhost:8765';

// RH850 2-wire UART boot protocol (Renesas standard boot firmware).
// Packet: SOH(0x01 cmd / 0x81 data) + LEN(2B BE = len of COM+DATA) + COM + DATA.. + SUM + ETX(0x03).
// SUM = (0x100 - sum(LEN..DATA)) & 0xFF. Ref: RX65N boot spec + I-CAN-hack/chipolino.
const hex = (b) => b.toString(16).toUpperCase().padStart(2, '0');

function pkt(soh, com, data = []) {
  const len = 1 + data.length;
  const lnh = (len >> 8) & 0xff, lnl = len & 0xff;
  let sum = lnh + lnl + com; for (const d of data) sum += d;
  const chk = (0x100 - (sum & 0xff)) & 0xff;
  return [soh, lnh, lnl, com, ...data, chk, 0x03];
}
const cmd = (com, data = []) => pkt(0x01, com, data);   // host -> device command
const ack = (com, data = []) => pkt(0x81, com, data);   // host -> device "send data" ack
const u32be = (n) => [(n >>> 24) & 0xff, (n >>> 16) & 0xff, (n >>> 8) & 0xff, n & 0xff];
const fromHexStr = (s) => new Uint8Array(((s || '').match(/../g) || []).map((h) => parseInt(h, 16)));
const toHexStr = (u8) => Array.from(u8).map((b) => b.toString(16).padStart(2, '0')).join('');
// CRC-32 (IEEE, sama dengan yang ditampilkan RFP) untuk cocokkan file .bin.
const CRC_TABLE = (() => { const t = new Uint32Array(256); for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; } return t; })();
const crc32 = (u8) => { let c = 0xFFFFFFFF; for (let i = 0; i < u8.length; i++) c = CRC_TABLE[(c ^ u8[i]) & 0xFF] ^ (c >>> 8); return ((c ^ 0xFFFFFFFF) >>> 0).toString(16).toUpperCase().padStart(8, '0'); };

// Device Code (8 byte dari DeviceType 0x38) -> nama populer. Dikonfirmasi hardware.
const KNOWN_RH850 = {
  '1000FF4000480000': 'R7F701021',
};

// Profil model: menentukan nama device + peta area flash (dipilih user via dropdown).
const RH850_MODELS = [
  {
    id: 'scoopy-k2f', name: 'Scoopy K2F / Genio — R7F701021', code: '1000FF4000480000',
    areas: [
      { kind: 0, label: 'Code Flash 1', start: 0x00000000, end: 0x0003FFFF, size: 0x40000 },
      { kind: 1, label: 'Data Flash 1', start: 0xFF200000, end: 0xFF207FFF, size: 0x8000 },
      { kind: 2, label: 'User Boot Area 1', start: 0x01000000, end: 0x01007FFF, size: 0x8000 },
    ],
  },
  { id: 'generic', name: 'RH850 Generic (auto-map R7F701021)', code: '', areas: null },
];

const LEVELS = { INFO: 'text-slate-300', SUCCESS: 'text-emerald-300', ERROR: 'text-red-300', WARN: 'text-amber-300', UART: 'text-cyan-300' };

export const Rh850FlashTool = ({ onBusyChange }) => {
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const [connected, setConnected] = useState(false);
  const [ports, setPorts] = useState([]);
  const [port, setPort] = useState('');
  const [portsLoading, setPortsLoading] = useState(false);
  const [clock, setClock] = useState('8');
  const [device, setDevice] = useState(null);
  const [areas, setAreas] = useState([]);
  const [progress, setProgress] = useState(0);
  const [copied, setCopied] = useState(false);
  const [saveAsReq, setSaveAsReq] = useState(null);
  const [clockAsk, setClockAsk] = useState(null);
  const [clockInput, setClockInput] = useState('8');
  const [idcode, setIdcode] = useState('');
  const [fastBaud, setFastBaud] = useState(true);
  const [profileId, setProfileId] = useState('scoopy-k2f');
  const [writeOpen, setWriteOpen] = useState(false);
  const [writeArea, setWriteArea] = useState(0);
  const [writeErase, setWriteErase] = useState(true);
  const [writeVerify, setWriteVerify] = useState(true);
  const [writeFile, setWriteFile] = useState(null);
  const [dlgMode, setDlgMode] = useState('write');
  const fileRef = useRef(null);
  const [readOpen, setReadOpen] = useState(false);
  const [readArea, setReadArea] = useState(0);
  const mgrRef = useRef(null);
  const logRef = useRef(null);

  useEffect(() => { onBusyChange?.(busy); }, [busy, onBusyChange]);
  useEffect(() => { const el = logRef.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }); }, [lines]);

  const log = useCallback((kind, msg) => {
    if (kind === 'UART' || isSensitiveLog(msg)) return;
    setLines((p) => [...p.slice(-800), { kind, msg, t: new Date().toLocaleTimeString('en-GB', { hour12: false }) }]);
  }, []);

  const loadPorts = useCallback(async () => {
    if (connected || busy) { log('WARN', 'Session active — port scan skipped.'); return; }
    setPortsLoading(true);
    const r = await fetchBridgePorts(BRIDGE_URL);
    setPortsLoading(false);
    if (!r.online) { setPorts([]); log('ERROR', 'TRMD Engine not detected — run TRMDEngine.exe.'); return; }
    const list = (r.ports || []).filter((p) => p && p.port && /^COM\d+$/i.test(p.port));
    setPorts(list);
    if (list.length && !port) {
      const ftdi = list.find((p) => p.kind === 'usb' || /ft|ftdi|cp210|ch34/i.test(p.name || ''));
      setPort((ftdi || list[0]).port);
    }
    log(list.length ? 'INFO' : 'WARN', list.length
      ? `Ports detected: ${list.map((p) => p.port).join(', ')}`
      : 'No COM port found — connect the FTDI TTL 5 V adapter and install the VCP driver.');
  }, [log, port, connected, busy]);

  const portsOnce = useRef(false);
  useEffect(() => { if (portsOnce.current) return; portsOnce.current = true; loadPorts(); /* eslint-disable-next-line */ }, []);

  const mgr = useCallback(async () => {
    if (!mgrRef.current) mgrRef.current = getSharedManager();
    const m = mgrRef.current;
    const wsOpen = m.bridge && m.bridge.ws && m.bridge.ws.readyState === 1;
    if (m.mode !== 'bridge' || !wsOpen) await m.connectBridgeWsOnly({ url: BRIDGE_URL });
    return m;
  }, []);

  const xchg = useCallback(async (m, bytes, timeout = 1200) => {
    const rx = await m.bridge.writeRead(bytes, timeout);
    return Array.from(rx || []);
  }, []);

  const askClock = useCallback(() => new Promise((resolve) => setClockAsk({ resolve })), []);

  const FALLBACK_AREAS = [
    { kind: 0, label: 'Code Flash 1', start: 0x00000000, end: 0x0003FFFF, size: 0x40000 },
    { kind: 1, label: 'Data Flash 1', start: 0xFF200000, end: 0xFF207FFF, size: 0x8000 },
    { kind: 2, label: 'User Boot Area 1', start: 0x01000000, end: 0x01007FFF, size: 0x8000 },
  ];

  // Urutan boot RH850 2-wire UART yang sudah terbukti pada R7F701021 (log user):
  // open raw @9600 -> burst 0x00 (auto-baud) -> 0x55/C1 -> DeviceType(38) -> Set Freq(32)
  // -> Set Baud(34, 4-byte BE) -> host pindah baud. mhzOverride != null => tanpa popup clock.
  const handshake = useCallback(async (m, mhzOverride = null, syncMs = 15000) => {
    log('INFO', `Opening ${port || 'AUTO'} @ 9600 bps…`);
    // COM bisa drop sesaat saat ECU di-reset -> retry otomatis bila "port is closed".
    for (let attempt = 1; ; attempt++) {
      try { await m.bridge.open({ device: port || 'auto', baud: 9600, dll: '', protocol: 'raw', raw: 1 }); break; }
      catch (e) {
        if (attempt >= 4 || !/port is closed|access|denied|busy/i.test(e.message)) throw e;
        log('WARN', `Port not ready — attempt ${attempt}/3…`);
        await new Promise((r) => setTimeout(r, 800));
      }
    }

    log('WARN', `Waiting for ECU reset — synchronizing for ${Math.round(syncMs / 1000)} s…`);
    let acked = false; const t0 = Date.now();
    const burst = new Array(16).fill(0x00);
    while (!acked && Date.now() - t0 < syncMs) {
      let r;
      try { r = await xchg(m, burst, 180); }
      catch (e) {
        // COM drop saat ECU di-reset (USB re-enumerasi) -> buka ulang port lalu lanjut sync.
        if (!/port is closed|not open|access|denied/i.test(e.message)) throw e;
        log('WARN', 'Port dropped during reset — reopening…');
        await new Promise((res) => setTimeout(res, 700));
        try { await m.bridge.closePort(); } catch (_) {}
        try { await m.bridge.open({ device: port || 'auto', baud: 9600, dll: '', protocol: 'raw', raw: 1 }); }
        catch (_) { await new Promise((res) => setTimeout(res, 500)); }
        continue;
      }
      if (r.length) log('UART', `Sync ← ${r.slice(0, 16).map(hex).join(' ')}`);
      if (r.includes(0x00)) acked = true;
      setProgress(Math.min(60, Math.round(((Date.now() - t0) / syncMs) * 60)));
    }
    if (!acked) { log('ERROR', 'No response from ECU — reset the ECU during synchronization and check RX/TX/GND wiring.'); return { ok: false }; }

    const c = await xchg(m, [0x55], 500);
    log('UART', `Sync 0x55 ← ${c.map(hex).join(' ') || '—'}`);
    if (!c.includes(0xc1)) log('WARN', 'Non-standard sync response — continuing.');
    log('SUCCESS', 'Synchronization successful.');

    await xchg(m, cmd(0x38), 600);
    const dt = await xchg(m, ack(0x38), 1200);
    log('UART', `Device Type ← ${dt.map(hex).join(' ') || '—'}`);
    let cpa = null;
    if (dt.length >= 28 && dt[0] === 0x81) {
      const d = dt.slice(4);
      const be = (o) => ((d[o] << 24) | (d[o + 1] << 16) | (d[o + 2] << 8) | d[o + 3]) >>> 0;
      const osa = be(8), osi = be(12); cpa = be(16); const cpi = be(20);
      log('INFO', `Input clock ${osi / 1e6}–${osa / 1e6} MHz · System clock ${cpi / 1e6}–${cpa / 1e6} MHz`);
      const code = d.slice(0, 8).map(hex).join('');
      if (code) { setDevice(code); }
    }

    const mhz = mhzOverride != null ? mhzOverride : await askClock();
    if (!mhz) { log('INFO', 'Cancelled — clock not set.'); return { ok: false }; }
    setClock(mhz);
    const oc = u32be(Math.round(parseFloat(mhz) * 1e6));
    const cc = u32be(cpa || 80000000);
    const f1 = await xchg(m, cmd(0x32, [...oc, ...cc]), 800);
    log('UART', `Set Frequency ← ${f1.map(hex).join(' ') || '—'}`);
    if (f1[3] === 0xb2) { log('ERROR', 'Set Frequency rejected — check clock value.'); return { ok: false }; }
    await xchg(m, ack(0x32), 1000);

    const tgtBaud = fastBaud ? 115200 : 9600;
    const r34 = await xchg(m, cmd(0x34, u32be(tgtBaud)), 700);
    log('UART', `Set Baud Rate ${tgtBaud} ← ${r34.map(hex).join(' ') || '—'}`);
    if (r34[3] !== 0x34) { log('ERROR', `Set Baud Rate ${tgtBaud} rejected.`); return { ok: false }; }
    if (fastBaud) {
      try { await m.bridge.setBaud(115200); log('INFO', 'Host baud rate: 115200 bps.'); }
      catch (e) { log('WARN', `Failed to change host baud rate: ${e.message}`); }
    } else {
      log('INFO', 'Baud rate kept at 9600 bps.');
    }

    // Fase Authentication. Urutan wajib (ref chipolino/Renesas boot):
    // Inquiry(0x00) -> is_set_IDCODE(0x2C) -> IDCODE check(0x30, 16 byte).
    const inq = await xchg(m, cmd(0x00), 800);
    log('UART', `Inquiry ← ${inq.map(hex).join(' ') || '—'}`);
    if (inq.includes(0xdc)) {
      log('ERROR', 'Serial programmer access disabled by device (0xDC).');
      return { ok: false };
    }
    const chk = await xchg(m, cmd(0x2c), 800);
    log('UART', `ID Code Status ← ${chk.map(hex).join(' ') || '—'}`);
    // 0x30 tidak dikirim di sini — Read dicoba dulu; auth dilakukan hanya bila
    // device membalas flow-error (masih di Authentication phase). Lihat readArea15.
    return { ok: true };
  }, [port, xchg, askClock, log, fastBaud]);

  // Kirim IDCODE check (0x30) + 16 byte. idHex kosong => default FF×16.
  // Operation command (0x30) bisa butuh waktu proses internal lebih lama dari generic
  // command, jadi pakai RX window besar (3500ms) + satu retry window lebih besar lagi.
  const doAuth = useCallback(async (m) => {
    const clean = (idcode || '').replace(/[^0-9a-fA-F]/g, '');
    let id = new Array(16).fill(0xff);
    const usingDefault = clean.length !== 32;
    if (!usingDefault) id = clean.match(/.{2}/g).map((h) => parseInt(h, 16));
    else if (clean.length) log('ERROR', 'ID Code must be 32 hex digits (16 bytes) — input ignored.');
    log('INFO', usingDefault ? 'ID Authentication using default (FF×16).' : 'ID Authentication using entered ID Code.');
    let a1 = await xchg(m, cmd(0x30, id), 3500);
    if (a1.length === 0) { log('WARN', 'ID Authentication no response — retrying…'); a1 = await xchg(m, cmd(0x30, id), 6000); }
    log('UART', `ID Authentication ← ${a1.map(hex).join(' ') || '—'}`);
    if (a1.length === 0) {
      log('ERROR', usingDefault
        ? 'ID Authentication still has no response. Enter the ECU ID Code (32 hex) if required and retry.'
        : 'ID Authentication with this ID Code has no response — check the ID Code byte order.');
      return false;
    }
    if (a1.includes(0xdb)) { log('ERROR', 'ID Authentication failed — wrong ID Code.'); return false; }
    if (a1[0] === 0x81 && a1[3] === 0x30 && !a1.includes(0xc3)) { log('SUCCESS', 'ID Authentication successful.'); return true; }
    log('WARN', `Unknown ID Authentication response: ${a1.map(hex).join(' ')}`);
    return false;
  }, [idcode, xchg, log]);

  const doConnect = useCallback(async () => {
    setBusy(true); setProgress(0); setDevice(null); setAreas([]);
    try {
      const m = await mgr();
      const r = await handshake(m, null);
      if (!r.ok) return;
      const prof = RH850_MODELS.find((p) => p.id === profileId);
      setAreas((prof && prof.areas) ? prof.areas : FALLBACK_AREAS);
      setConnected(true);
      log('SUCCESS', 'Connection ready — select Read, Write, or Verify.');
    } catch (e) { log('ERROR', `Connection failed: ${e.message}`); }
    finally { setBusy(false); setProgress(0); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mgr, handshake, log, profileId]);

  const doDisconnect = useCallback(async () => {
    try { const m = mgrRef.current; if (m && m.bridge) await m.bridge.close(); } catch (_) {}
    mgrRef.current = null;
    setConnected(false); setDevice(null); setAreas([]); log('INFO', 'Connection closed.');
  }, [log]);

  // Baca satu area via perintah 0x15, data datang per 0x400 byte.
  // Operation command (0x15) dipisah jelas: C3 = flow error eksplisit; kosong = tanpa balasan
  // (bukan bukti lock) -> retry window panjang dulu, baru coba auth.
  const readArea15 = useCallback(async (m, start, end) => {
    const total = end - start + 1;
    const out = new Uint8Array(total);
    const rd = [...u32be(start), ...u32be(end)];
    // Read-prep E1/RFP ("Setting the target device" x2 di log RFP): Signature request (0x3A)
    // lalu command 0x71 param 0xFF. WAJIB sebelum 0x15 — tanpa ini device meng-ignore Read
    // (ref cc-sw: capture logic-analyzer E1 flash-read: SIGREQ -> 0x71(FF) -> READ 0x15).
    const sig = await xchg(m, cmd(0x3a), 1500);
    log('UART', `Signature ← ${sig.map(hex).join(' ') || '—'}`);
    const sigd = await xchg(m, ack(0x3a), 1500);
    log('UART', `Signature data ← ${sigd.map(hex).join(' ') || '—'}`);
    const p71 = await xchg(m, cmd(0x71, [0xff]), 1500);
    log('UART', `Pre-read ← ${p71.map(hex).join(' ') || '—'}`);
    let r = await xchg(m, cmd(0x15, rd), 3500);
    log('UART', `Read ← ${r.map(hex).join(' ') || '—'}`);
    if (r.includes(0xda)) throw new Error('0xDA protection error — read protection active.');
    if (r.length === 0) {
      log('WARN', 'Read no response — retrying…');
      r = await xchg(m, cmd(0x15, rd), 6000);
      log('UART', `Read (retry) ← ${r.map(hex).join(' ') || '—'}`);
    }
    const flowErr = r[3] === 0x95 && r.includes(0xc3);
    if (flowErr || r.length === 0) {
      log('WARN', flowErr ? 'Read rejected — running ID Authentication…' : 'Read no response — running ID Authentication…');
      const okAuth = await doAuth(m);
      if (!okAuth) throw new Error(flowErr ? 'ID Authentication failed — read cancelled.' : 'No response to Read or ID Authentication.');
      r = await xchg(m, cmd(0x15, rd), 6000);
      log('UART', `Read (retry) ← ${r.map(hex).join(' ') || '—'}`);
      if (r.includes(0xda)) throw new Error('0xDA protection error — read protection active.');
    }
    if (r.length === 0) throw new Error('ECU did not respond to Read — check clock and repeat ECU reset.');
    if (r.includes(0xdb)) throw new Error('0xDB — wrong ID Code. Enter the ECU ID Code (16 bytes).');
    if (!(r[0] === 0x81 && r[3] === 0x15)) throw new Error(`unexpected read response: ${r.map(hex).join(' ')}`);
    let got = 0;
    while (got < total) {
      const resp = await xchg(m, ack(0x15), 2000);
      if (resp.length < 6 || resp[0] !== 0x81) throw new Error(`corrupt data frame @${hex(start + got)}: ${resp.slice(0, 8).map(hex).join(' ')}`);
      const len = ((resp[1] << 8) | resp[2]) - 1; // dikurangi COM(0x15)
      const payload = resp.slice(4, 4 + len);
      for (let i = 0; i < payload.length && got + i < total; i++) out[got + i] = payload[i];
      got += len;
      setProgress(Math.min(99, Math.round((got / total) * 100)));
    }
    return out;
  }, [xchg, log, doAuth]);

  const doRead = useCallback(async () => {
    const area = areas[readArea];
    if (!area) { log('ERROR', 'Select a flash area first.'); return; }
    setReadOpen(false); setBusy(true); setProgress(0);
    try {
      const m = await mgr();
      // Setelah RESET OFF→ON device kembali ke boot phase: buka ulang port & ulangi
      // handshake (tanpa popup clock) supaya port pasti terbuka sebelum Read(0x15).
      log('INFO', 'Reconnecting after ECU reset…');
      try { await m.bridge.closePort(); } catch (_) {}
      const hs = await handshake(m, clock, 20000);
      if (!hs.ok) { log('ERROR', 'Reconnect failed — reset the ECU again.'); return; }
      setProgress(0);
      log('INFO', `Reading ${area.label} ${hex(area.start)}–${hex(area.end)} (${(area.size / 1024).toFixed(0)} KB)…`);
      let bytes;
      try {
        const res = await m.bridge.rh850Read({ start: area.start, end: area.end }, (msg) => {
          if (msg.log) log(msg.level || 'UART', msg.log);
          else if (msg.total) setProgress(Math.min(99, Math.round((msg.progress / msg.total) * 100)));
        });
        bytes = fromHexStr(res.data);
      } catch (e) {
        if (/unknown cmd/i.test(e.message)) {
          log('WARN', 'Outdated TRMD Engine — update to the latest version.');
          bytes = await readArea15(m, area.start, area.end);
        } else throw e;
      }
      let blank = 0; for (let i = 0; i < bytes.length; i++) if (bytes[i] === 0xff) blank++;
      setSaveAsReq({ bytes, name: `rh850-${device || 'dev'}-${hex(area.start)}-${Date.now()}.bin` });
      log('SUCCESS', `Read complete — ${bytes.length} bytes (${((blank / bytes.length) * 100).toFixed(1)}% blank).`);
    } catch (e) { log('ERROR', `Read failed: ${e.message}`); }
    finally { setBusy(false); setProgress(0); }
  }, [areas, readArea, mgr, handshake, clock, readArea15, device, log]);

  const onPickFile = useCallback(async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const buf = new Uint8Array(await f.arrayBuffer());
    const crc = crc32(buf);
    setWriteFile({ name: f.name, data: buf, crc });
    log('INFO', `File loaded: ${f.name} · ${buf.length} bytes · CRC-32 ${crc}`);
    e.target.value = '';
  }, [log]);

  const doWrite = useCallback(async () => {
    const area = areas[writeArea];
    if (!area) { log('ERROR', 'Select a flash area first.'); return; }
    if (!writeFile) { log('ERROR', 'Load a .bin file first.'); return; }
    if (writeFile.data.length !== area.size) { log('ERROR', `File size (${writeFile.data.length} bytes) does not match the area (${area.size} bytes).`); return; }
    setWriteOpen(false); setBusy(true); setProgress(0);
    try {
      const m = await mgr();
      log('INFO', 'Reconnecting after ECU reset…');
      try { await m.bridge.closePort(); } catch (_) {}
      const hs = await handshake(m, clock, 20000);
      if (!hs.ok) { log('ERROR', 'Reconnect failed — reset the ECU again.'); return; }
      setProgress(0);
      log('WARN', `Write ${area.label} ${hex(area.start)}–${hex(area.end)} (${(area.size / 1024).toFixed(0)} KB) · ${[writeErase && 'Erase', 'Program', writeVerify && 'Verify'].filter(Boolean).join(' › ')} — do not disconnect power.`);
      const res = await m.bridge.rh850Write({ start: area.start, end: area.end, data: toHexStr(writeFile.data), erase: writeErase, verify: writeVerify }, (msg) => {
        if (msg.log) log(msg.level || 'UART', msg.log);
        else if (msg.total) setProgress(Math.min(99, Math.round((msg.progress / msg.total) * 100)));
      });
      if (writeVerify && res.verified === false) log('ERROR', `Verify failed — data mismatch at offset ${res.mismatch}. Restore from backup.`);
      else log('SUCCESS', `Write complete — ${res.written} bytes${writeVerify ? ' · Verify OK' : ''}.`);
    } catch (e) {
      if (/unknown cmd/i.test(e.message)) log('ERROR', 'Outdated TRMD Engine — update to the latest version.');
      else log('ERROR', `Write failed: ${e.message}`);
    } finally { setBusy(false); setProgress(0); }
  }, [areas, writeArea, writeFile, writeErase, writeVerify, mgr, handshake, clock, log]);

  const doVerify = useCallback(async () => {
    const area = areas[writeArea];
    if (!area) { log('ERROR', 'Select a flash area first.'); return; }
    if (!writeFile) { log('ERROR', 'Load a .bin file first.'); return; }
    if (writeFile.data.length !== area.size) { log('ERROR', `File size (${writeFile.data.length} bytes) does not match the area (${area.size} bytes).`); return; }
    setWriteOpen(false); setBusy(true); setProgress(0);
    try {
      const m = await mgr();
      log('INFO', 'Reconnecting after ECU reset…');
      try { await m.bridge.closePort(); } catch (_) {}
      const hs = await handshake(m, clock, 20000);
      if (!hs.ok) { log('ERROR', 'Reconnect failed — reset the ECU again.'); return; }
      setProgress(0);
      log('INFO', `Verifying ${area.label} ${hex(area.start)}–${hex(area.end)} against ${writeFile.name}…`);
      const res = await m.bridge.rh850Read({ start: area.start, end: area.end }, (msg) => {
        if (msg.log) log(msg.level || 'UART', msg.log);
        else if (msg.total) setProgress(Math.min(99, Math.round((msg.progress / msg.total) * 100)));
      });
      const ecu = fromHexStr(res.data); const f = writeFile.data;
      let mism = 0, first = -1;
      for (let i = 0; i < f.length; i++) if (ecu[i] !== f[i]) { mism++; if (first < 0) first = i; }
      if (mism === 0) log('SUCCESS', `Verify OK — ${f.length} bytes identical.`);
      else log('ERROR', `Verify failed — ${mism} bytes differ, first at ${hex(area.start + first)} (ECU ${hex(ecu[first])}, file ${hex(f[first])}).`);
    } catch (e) { log('ERROR', `Verify failed: ${e.message}`); }
    finally { setBusy(false); setProgress(0); }
  }, [areas, writeArea, writeFile, mgr, handshake, clock, log]);

  const copyLog = useCallback(() => {
    const t = lines.map((l) => `[${l.t}] ${l.msg}`).join('\n');
    navigator.clipboard?.writeText(t); setCopied(true); setTimeout(() => setCopied(false), 1500);
  }, [lines]);

  const BTN = 'h-[38px] px-3 rounded-[5px] inline-flex items-center justify-center gap-2 text-[13px] font-semibold transition-colors disabled:opacity-35';
  const fmt = (n) => '0x' + (n >>> 0).toString(16).toUpperCase().padStart(8, '0');

  return (
    <div className="rounded-xl border border-cyan-900/50 bg-[#0a0f16] overflow-hidden h-full flex flex-col" data-testid="rh850-tool">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-cyan-900/40 bg-[#0c1420]">
        <Cpu className="h-4 w-4 text-cyan-400" />
        <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-bold">RH850 Flash Tool</span>
        <span className="ml-auto flex items-center gap-1.5 text-[11px] font-mono-tt">
          <span className={`inline-block h-2 w-2 rounded-full ${connected ? 'bg-emerald-400' : 'bg-slate-600'}`} data-testid="rh850-conn-dot" />
          {connected ? 'Online' : 'Offline'}
          {busy && progress > 0 && <span data-testid="rh850-pct" className="ml-2 px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 font-bold">{progress}%</span>}
        </span>
      </div>
      <div className="grid grid-cols-[380px_1fr] max-lg:grid-cols-1 [.ft-narrow_&]:grid-cols-1 p-4 gap-4 flex-1 min-h-0 items-start">
      {clockAsk && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="rh850-clock-dialog">
          <div className="w-[min(92vw,420px)] rounded-xl border border-cyan-500/40 bg-[#0b1017] px-5 py-4">
            <div className="font-bold text-[15px] text-cyan-300">SET CLOCK</div>
            <div className="text-xs text-zinc-400 mt-1 mb-3">RH850 main clock in MHz (crystal value, e.g. 8 / 16 / 20).</div>
            <input autoFocus value={clockInput} onChange={(e) => setClockInput(e.target.value.replace(/[^0-9.]/g, ''))}
              data-testid="rh850-clock-input"
              className="w-full h-10 rounded-[5px] bg-[#0b0e13] border border-[#262c36] px-3 text-sm text-zinc-100" />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => { const a = clockAsk; setClockAsk(null); a.resolve(null); }} data-testid="rh850-clock-cancel"
                className="h-9 px-4 rounded-[5px] border border-[#262c36] bg-[#161a21] text-zinc-300 text-[12px] font-semibold">Cancel</button>
              <button onClick={() => { const a = clockAsk; setClockAsk(null); a.resolve(clockInput || '8'); }} data-testid="rh850-clock-ok"
                className="h-9 px-5 rounded-[5px] border border-cyan-500/50 bg-cyan-500/15 text-cyan-300 text-[12px] font-bold">OK</button>
            </div>
          </div>
        </div>
      )}

      {readOpen && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="rh850-read-dialog">
          <div className="w-[min(92vw,460px)] rounded-xl border border-[#2a2f39] bg-[#0b1017] px-5 py-4">
            <div className="font-bold text-[14px] text-zinc-200 mb-3">READ MEMORY</div>
            <label className="block text-xs text-zinc-400 mb-1">Select Area</label>
            <select value={readArea} onChange={(e) => setReadArea(Number(e.target.value))} data-testid="rh850-read-area"
              className="w-full h-9 rounded-[5px] bg-[#0b0e13] border border-[#262c36] px-2 text-[12px] text-zinc-100">
              {areas.map((a, i) => <option key={i} value={i}>{`${a.label} · ${fmt(a.start)}–${fmt(a.end)} · ${(a.size / 1024).toFixed(0)} KB`}</option>)}
            </select>
            <div className="mt-3 rounded-[5px] border border-amber-600/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200 leading-relaxed" data-testid="rh850-reset-note">
              <b>Reset ECU before continuing:</b> press <b>OFF</b> button → press <b>ON</b> button, then click <b>Continue Read</b>.
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setReadOpen(false)} data-testid="rh850-read-cancel"
                className="h-9 px-4 rounded-[5px] border border-[#262c36] bg-[#161a21] text-zinc-300 text-[12px] font-semibold">Cancel</button>
              <button onClick={doRead} data-testid="rh850-read-start"
                className="h-9 px-5 rounded-[5px] border border-red-500/50 bg-red-500/15 text-red-300 text-[12px] font-bold">Continue Read</button>
            </div>
          </div>
        </div>
      )}

      {writeOpen && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="rh850-write-dialog">
          <div className={`w-[min(92vw,480px)] rounded-xl border ${dlgMode === 'write' ? 'border-red-700/50' : 'border-cyan-700/50'} bg-[#0b1017] px-5 py-4`}>
            <div className={`font-bold text-[14px] mb-1 ${dlgMode === 'write' ? 'text-red-300' : 'text-cyan-300'}`}>
              {dlgMode === 'write' ? 'WRITE MEMORY — DESTRUCTIVE' : 'VERIFY — compare ECU vs file (read-only)'}
            </div>
            {dlgMode === 'write' && (
              <div className="text-[11px] text-amber-300/80 mb-3 leading-relaxed">
                Make sure you have a backup dump and DO NOT disconnect power during the process.
              </div>
            )}
            <label className="block text-xs text-zinc-400 mb-1">Select Area</label>
            <select value={writeArea} onChange={(e) => setWriteArea(Number(e.target.value))} data-testid="rh850-write-area"
              className="w-full h-9 rounded-[5px] bg-[#0b0e13] border border-[#262c36] px-2 text-[12px] text-zinc-100">
              {areas.map((a, i) => <option key={i} value={i}>{`${a.label} · ${fmt(a.start)}–${fmt(a.end)} · ${(a.size / 1024).toFixed(0)} KB`}</option>)}
            </select>
            <button onClick={() => fileRef.current?.click()} data-testid="rh850-write-pick"
              className="mt-2 h-9 w-full rounded-[5px] border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 text-[12px] font-semibold inline-flex items-center justify-center gap-2">
              <Upload size={14} /> Load .bin File
            </button>
            <div className="mt-1 text-[11px] font-mono text-zinc-400 truncate" data-testid="rh850-write-filename">
              {writeFile ? `${writeFile.name} · ${writeFile.data.length} B` : 'No file loaded'}
            </div>
            {writeFile && (
              <div className="text-[11px] font-mono text-zinc-300" data-testid="rh850-write-crc">
                Offset: <span className="text-cyan-300">0x{(areas[writeArea]?.start ?? 0).toString(16).toUpperCase().padStart(8, '0')}</span>
                {' · '}CRC-32: <span className="text-emerald-300 font-bold">{writeFile.crc}</span>
              </div>
            )}
            {dlgMode === 'write' && (
              <div className="mt-2 text-[11px] text-zinc-400" data-testid="rh850-write-command">
                Command: <span className="text-zinc-200 font-semibold">{[writeErase && 'Erase', 'Program', writeVerify && 'Verify'].filter(Boolean).join(' >> ')}</span>
              </div>
            )}
            {dlgMode === 'write' && (<>
            <label className="mt-2 flex items-center gap-2 text-xs text-zinc-300 cursor-pointer select-none">
              <input type="checkbox" checked={writeErase} onChange={(e) => setWriteErase(e.target.checked)} data-testid="rh850-write-erase" className="h-3.5 w-3.5 accent-red-500" />
              Erase before program
            </label>
            <div className="text-[10px] text-zinc-500 ml-6 -mt-0.5 leading-snug">Block erase 8 KB/32 KB following the Code Flash 1 layout.</div>
            <label className="mt-1 flex items-center gap-2 text-xs text-zinc-300 cursor-pointer select-none">
              <input type="checkbox" checked={writeVerify} onChange={(e) => setWriteVerify(e.target.checked)} data-testid="rh850-write-verify" className="h-3.5 w-3.5 accent-emerald-500" />
              Verify after program (read-back &amp; compare)
            </label>
            </>)}
            <div className="mt-3 rounded-[5px] border border-amber-600/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200 leading-relaxed">
              <b>Reset ECU before continuing:</b> press <b>OFF</b> button → press <b>ON</b> button, then click <b>{dlgMode === 'write' ? 'Continue Write' : 'Continue Verify'}</b>.
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setWriteOpen(false)} data-testid="rh850-write-cancel"
                className="h-9 px-4 rounded-[5px] border border-[#262c36] bg-[#161a21] text-zinc-300 text-[12px] font-semibold">Cancel</button>
              <button onClick={dlgMode === 'write' ? doWrite : doVerify} disabled={!writeFile} data-testid="rh850-write-start"
                className={`h-9 px-5 rounded-[5px] border text-[12px] font-bold disabled:opacity-40 ${dlgMode === 'write' ? 'border-red-500/60 bg-red-600/25 text-red-200' : 'border-cyan-500/60 bg-cyan-600/20 text-cyan-200'}`}>
                {dlgMode === 'write' ? 'Continue Write' : 'Continue Verify'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3 min-w-0">
        <div className="rounded-lg border border-[#20242c] bg-[#101319] p-3 space-y-2 min-w-0">
          <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Model / ECU</label>
          <select value={profileId} onChange={(e) => setProfileId(e.target.value)} disabled={busy || connected} data-testid="rh850-model"
            className="w-full h-9 rounded-[5px] bg-[#0b0e13] border border-[#262c36] px-2 text-[12px] text-zinc-100">
            {RH850_MODELS.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">COM Port (FTDI TTL 5 V)</label>
          <div className="flex items-center gap-2 w-full min-w-0">
            <select value={port} onChange={(e) => setPort(e.target.value)} disabled={busy} data-testid="rh850-port"
              className="flex-1 min-w-0 w-full h-9 rounded-[5px] bg-[#0b0e13] border border-[#262c36] px-2 text-[12px] text-zinc-100 truncate">
              <option value="">Auto COM</option>
              {ports.map((p) => <option key={p.port} value={p.port}>{p.port} — {p.name}</option>)}
            </select>
            <button onClick={loadPorts} disabled={portsLoading || busy} data-testid="rh850-refresh-ports"
              className="shrink-0 h-9 px-3 rounded-[5px] border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 text-[12px] inline-flex items-center gap-1.5 disabled:opacity-40">
              <RefreshCw className={`h-3.5 w-3.5 ${portsLoading ? 'animate-spin' : ''}`} /> Ports
            </button>
          </div>
          <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">ID Code (optional · 16-byte hex, blank = FF×16)</label>
          <input value={idcode} onChange={(e) => setIdcode(e.target.value)} disabled={busy} data-testid="rh850-idcode"
            placeholder="e.g. FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFF"
            className="w-full h-9 rounded-[5px] bg-[#0b0e13] border border-[#262c36] px-2 text-[11px] font-mono text-zinc-100 truncate" />
          <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer select-none">
            <input type="checkbox" checked={fastBaud} onChange={(e) => setFastBaud(e.target.checked)} disabled={busy} data-testid="rh850-fast-baud"
              className="h-3.5 w-3.5 accent-cyan-500" />
            Baud rate 115200
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={connected ? doDisconnect : doConnect} disabled={busy} data-testid="rh850-connect"
              className={`${BTN} bg-[#1b4f72] hover:bg-[#21608c] text-white`}>
              <Plug size={15} /> {connected ? 'Disconnect' : 'Connect'}
            </button>
            <button onClick={() => { setReadArea(0); setReadOpen(true); }} disabled={busy || !connected || !areas.length} data-testid="rh850-read-memory"
              className={`${BTN} border border-[#5a2a24] bg-[#20110f] text-[#d98b7f] hover:bg-[#2a1613]`}>
              <HardDriveDownload size={15} /> Read Memory
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => { setDlgMode('write'); setWriteArea(0); setWriteOpen(true); }} disabled={busy || !connected || !areas.length} data-testid="rh850-write-memory"
              className={`${BTN} border border-red-600 bg-red-900/30 text-red-300 hover:bg-red-900/50`}>
              <Upload size={15} /> Write
            </button>
            <button onClick={() => { setDlgMode('verify'); setWriteArea(0); setWriteOpen(true); }} disabled={busy || !connected || !areas.length} data-testid="rh850-verify-memory"
              className={`${BTN} border border-[#262c36] bg-[#161a21] text-zinc-200 hover:bg-[#1d2230]`}>
              <Check size={15} /> Verify
            </button>
          </div>
          <input ref={fileRef} type="file" accept=".bin,application/octet-stream" onChange={onPickFile} className="hidden" data-testid="rh850-write-file" />
          <div className="grid grid-cols-2 gap-2 text-[12px]">
            <div><div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Clock</div><div className="text-zinc-200" data-testid="rh850-clock">{connected ? `${clock} MHz · ${fastBaud ? '115200' : '9600'}` : '—'}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Device</div><div className="text-zinc-200 truncate" data-testid="rh850-device" title={device || ''}>{device ? (KNOWN_RH850[device] || device) : '—'}</div></div>
          </div>
          {areas.length > 0 && (
            <div className="border-t border-[#20242c] pt-2 space-y-1" data-testid="rh850-areas">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Flash Areas</div>
              {areas.map((a, i) => (
                <div key={i} className="font-mono text-[11px] text-zinc-400">{`${a.label} · ${fmt(a.start)}–${fmt(a.end)} · ${(a.size / 1024).toFixed(0)} KB`}</div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="relative flex flex-col min-h-0 h-full rounded-lg border border-slate-800 bg-[#080b11] overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-800 bg-[#0c1017] shrink-0">
          <Cpu className="h-4 w-4 text-cyan-500" />
          <span className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">RH850 Console</span>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={copyLog} disabled={!lines.length} data-testid="rh850-copy"
              className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border transition-colors disabled:opacity-40 ${copied ? 'border-emerald-700 text-emerald-400' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}`}>
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} {copied ? 'Copied' : 'Copy'}
            </button>
            <button onClick={() => setLines([])} disabled={!lines.length} data-testid="rh850-clear"
              className="text-[10px] px-2 py-0.5 rounded border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-40">Clear</button>
          </div>
        </div>
        <div className="relative h-5 w-full tt-bar-track shrink-0 overflow-hidden" data-testid="rh850-progress">
          <div className="h-full tt-bar-fill transition-all" style={{ width: `${progress}%` }} />
          {busy && progress > 0 && <span data-testid="rh850-progress-text" className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-white drop-shadow">{progress}%</span>}
        </div>
        <div ref={logRef} className="p-4 overflow-y-auto font-mono-tt text-xs leading-relaxed space-y-1 break-all flex-1 min-h-[300px] lg:min-h-0" data-testid="rh850-log">
          {lines.length === 0 && <div className="text-slate-600">Select model &amp; COM port, then Connect…</div>}
          {lines.map((l, i) => (
            <div key={i} className={LEVELS[l.kind] || 'text-zinc-200'}>
              <span className="text-zinc-500">[{l.t}]</span> {l.msg}
            </div>
          ))}
        </div>
      </div>
      </div>
      {saveAsReq && (
        <SaveAsDialog open title="Save Read Result" defaultName={saveAsReq.name}
          onCancel={() => setSaveAsReq(null)}
          onSave={async (finalName) => { const req = saveAsReq; setSaveAsReq(null); const ok = await saveBinaryWithPicker(req.bytes, finalName, { onLog: log, toast }); if (ok) log('SUCCESS', `Saved: ${finalName}`); }} />
      )}
    </div>
  );
};
